import React from 'react';
import { NavLink } from 'react-router-dom';
import { UtensilsCrossed, ShoppingBag, Settings, LogOut, Tag, Tags, ClipboardList, MapPin, Receipt, Star, FileCheck, Hotel, Megaphone } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

export const Sidebar: React.FC = () => {
  const {
    user,
    logout
  } = useAuth();

  const navItems: { to: string; icon: React.ReactNode; label: string }[] = [];

  // Show Restaurants for Super Admin and Partner Admin
  const canSeeRestaurants = user?.role !== 'Support' && (
    user?.role === 'Resort Super Admin' ||
    user?.role === 'Supervisor' ||
    user?.role === 'Editor' ||
    user?.role === 'Partner Admin' ||
    user?.scope_type === 'all' ||
    user?.scope_type === 'restaurant');

  if (canSeeRestaurants) {
    navItems.push({
      to: '/restaurants',
      icon: <UtensilsCrossed size={20} />,
      label: 'Restaurants'
    });
  }

  // Show Groceries for Super Admin and Partner Admin
  const canSeeGroceries = user?.role !== 'Support' && (
    user?.role === 'Resort Super Admin' ||
    user?.role === 'Supervisor' ||
    user?.role === 'Editor' ||
    user?.role === 'Partner Admin' ||
    user?.scope_type === 'all' ||
    user?.scope_type === 'grocery');

  if (canSeeGroceries) {
    navItems.push({
      to: '/groceries',
      icon: <ShoppingBag size={20} />,
      label: 'Groceries'
    });
  }

  // Add Billing and Orders for Resort Super Admin and Partner Admin
  if (user?.role === 'Resort Super Admin' || user?.role === 'Supervisor' || user?.role === 'Partner Admin') {
    navItems.push({
      to: '/billing',
      icon: <Receipt size={20} />,
      label: 'Billing'
    });
    navItems.push({
      to: '/orders',
      icon: <ClipboardList size={20} />,
      label: 'Orders'
    });
    navItems.push({
      to: '/feedback',
      icon: <Star size={20} />,
      label: 'Feedback'
    });
  }

  // Support role only sees Orders and Feedback
  if (user?.role === 'Support') {
    navItems.push({
      to: '/orders',
      icon: <ClipboardList size={20} />,
      label: 'Orders'
    });
    navItems.push({
      to: '/feedback',
      icon: <Star size={20} />,
      label: 'Feedback'
    });
  }

  // Add Promo Codes for Resort Super Admin only
  if (user?.role === 'Resort Super Admin' || user?.role === 'Supervisor') {
    navItems.push({
      to: '/promo-codes',
      icon: <Tag size={20} />,
      label: 'Promo'
    });
  }

  if (user?.role === 'Resort Super Admin' || user?.role === 'Supervisor' || user?.role === 'Editor') {
    navItems.push({
      to: '/push-campaigns',
      icon: <Megaphone size={20} />,
      label: 'Push Campaigns'
    });
  }

  // Add Resorts for Resort Super Admin only
  if (user?.role === 'Resort Super Admin' || user?.role === 'Supervisor') {
    navItems.push({
      to: '/resorts',
      icon: <MapPin size={20} />,
      label: 'Resorts'
    });
  }

  // Add unified Food Tags for Super Admin, Supervisor (read-only) and Editor
  if (user?.role === 'Resort Super Admin' || user?.role === 'Supervisor' || user?.role === 'Editor') {
    navItems.push({
      to: '/food-tags',
      icon: <Tags size={20} />,
      label: 'Food Tags'
    });
  }

  // Add the accommodation catalogue for the roles that curate master data
  if (user?.role === 'Resort Super Admin' || user?.role === 'Supervisor' || user?.role === 'Editor') {
    navItems.push({
      to: '/accommodations',
      icon: <Hotel size={20} />,
      label: 'Accommodations'
    });
  }

  // Add Onboarding Docs for Partner Admin, Super Admin and Supervisor
  if (user?.role === 'Resort Super Admin' || user?.role === 'Supervisor' || user?.role === 'Partner Admin') {
    navItems.push({
      to: '/partner-onboarding',
      icon: <FileCheck size={20} />,
      label: 'Onboarding Docs'
    });
  }

  // Add Settings for all authenticated users (Change Password is available to all)
  navItems.push({
    to: '/settings',
    icon: <Settings size={20} />,
    label: 'Settings'
  });

  return (
    <aside className="w-64 bg-alps-primary text-white hidden md:flex flex-col min-h-0">
      {/* Logo */}
      <div className="p-6">
        <h2 className="text-xl font-bold">Resort Admin</h2>
      </div>
      {/* Navigation */}
      <nav className="flex-1 px-4 py-2 overflow-y-auto">
        <ul className="space-y-1">
          {navItems.map(item => (
            <li key={item.to}>
              <NavLink
                to={item.to}
                className={({ isActive }) =>
                  `flex items-center px-4 py-3 rounded-lg transition-colors ${
            isActive
                      ? 'bg-alps-primary-dark text-white'
                      : 'text-white hover:bg-alps-primary-dark'
                  }`
                }
              >
                {item.icon}
                <span className="ml-3">{item.label}</span>
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>
      {/* User Info & Logout */}
      <div className="p-4 border-t border-alps-primary-dark">
        <div className="flex items-center mb-4">
          <div className="w-10 h-10 rounded-full bg-alps-primary-dark flex items-center justify-center">
            <span className="text-lg font-medium">{user?.name?.[0]}</span>
          </div>
          <div className="ml-3">
            <p className="text-sm font-medium">{user?.name}</p>
            <p className="text-xs text-white capitalize">{user?.role}</p>
          </div>
        </div>
        <button
          onClick={logout}
          className="flex items-center w-full px-4 py-2 text-white rounded-lg hover:bg-alps-primary-dark transition-colors"
        >
          <LogOut size={20} />
          <span className="ml-3">Logout</span>
        </button>
      </div>
    </aside>
  );
};
